import type { ReactNode } from 'react';

import type { DerivedConfidence } from '@/lib/db';
import { StatusBadge } from '@/components/status-badge';
import { formatCount, formatDecimalHours, formatLocalTime, localZoneLabel } from '@/lib/format';
import type { DateFormat } from '@/lib/format';
import {
  ALTITUDE_UNIT_LABEL,
  DISTANCE_UNIT_LABEL,
  SPEED_UNIT_LABEL,
  convertAltitude,
  convertDistance,
  convertSpeed,
  type UnitPreferences,
} from '@/lib/units';

import { SOURCE_LABEL } from './labels';
import { airTimeMinutes, type FlightSummary } from './summary';

/**
 * What the track says about a flight: the four numbers worth reading first,
 * then the clock.
 *
 * Every value here is derived — `PLAN.md` §2, the track is evidence, not
 * truth — so the panel says so out loud, in the confidence badge and in the
 * footnote, rather than presenting a guessed block time as a fact. The logbook
 * entry beside it is the record; this is the source it was drafted from.
 */

const CONFIDENCE_LABEL: Record<DerivedConfidence, string> = {
  high: 'High confidence',
  partial: 'Partial coverage',
  low: 'Low confidence',
};

/** What the confidence means, on hover — the panel used to carry it as a footnote. */
const CONFIDENCE_HINT: Record<DerivedConfidence, string> = {
  high: 'Derived from the track. The logbook entry is the record.',
  partial: 'Coverage gaps in the track. Times and totals may be short.',
  low: 'Too little of the track to derive reliable times. Check against the logbook.',
};

function Tile({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <div className="border-border bg-surface-sunken rounded-container border px-3 py-2">
      <dt className="text-muted-foreground text-[0.625rem] font-medium tracking-wider uppercase">
        {label}
      </dt>
      <dd className="text-foreground mt-0.5 text-xl leading-tight font-semibold">
        <span className="figure">{value}</span>
        {unit === undefined ? null : (
          <span className="text-muted-foreground ml-1 text-xs font-normal">{unit}</span>
        )}
      </dd>
    </div>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-foreground">{value}</dd>
    </div>
  );
}

/** The date/time display settings both this panel and its callers read from `useSettings`. */
export interface FlightDateOptions {
  format?: DateFormat;
  timeZone?: string;
}

export interface FlightStatsProps {
  summary: FlightSummary;
  units: UnitPreferences;
  dateOptions?: FlightDateOptions;
}

export function FlightStats({ summary, units, dateOptions = {} }: FlightStatsProps) {
  const { stats, flight, track } = summary;
  const airMin = airTimeMinutes(summary);
  const zone =
    summary.startTime === undefined ? '' : localZoneLabel(summary.startTime, dateOptions);
  const time = (iso: string | undefined) =>
    iso === undefined ? null : (
      <>
        <span className="tabular">{formatLocalTime(iso, dateOptions)}</span>
        <span className="text-muted-foreground ml-1 text-xs">{zone}</span>
      </>
    );

  /*
   * "1" rather than "1 day · 0 night": most flights are day-only, and the
   * split is only worth reading when there is something on both sides.
   */
  const dayNight = (total: number | undefined, night: number | undefined) => {
    if (total === undefined) {
      return null;
    }
    const nightCount = night ?? 0;
    return nightCount === 0
      ? formatCount(total)
      : `${formatCount(total - nightCount)} day · ${formatCount(nightCount)} night`;
  };
  const takeoffs = dayNight(stats.takeoffCount, stats.nightTakeoffCount);
  const landings = dayNight(stats.landingCount, stats.nightLandingCount);

  return (
    <section
      className="border-border bg-surface rounded-container space-y-3 border p-3"
      aria-labelledby="flight-stats-heading"
      data-testid="flight-stats"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2
          id="flight-stats-heading"
          className="text-muted-foreground text-[0.625rem] font-medium tracking-wider uppercase"
        >
          From track
        </h2>
        <StatusBadge
          status={stats.confidence === 'high' ? 'approved' : 'none'}
          label={CONFIDENCE_LABEL[stats.confidence]}
          title={CONFIDENCE_HINT[stats.confidence]}
        />
      </div>

      <dl className="grid grid-cols-2 gap-2">
        <Tile
          label="Flight time"
          value={airMin === undefined ? '—' : formatDecimalHours(airMin / 60)}
          {...(airMin === undefined ? {} : { unit: 'hr' })}
        />
        <Tile
          label="Distance"
          value={
            stats.distanceNm === undefined
              ? '—'
              : formatCount(Math.round(convertDistance(stats.distanceNm, units.distance)))
          }
          {...(stats.distanceNm === undefined ? {} : { unit: DISTANCE_UNIT_LABEL[units.distance] })}
        />
        <Tile
          label="Max alt"
          value={
            stats.maxAltFt === undefined
              ? '—'
              : formatCount(Math.round(convertAltitude(stats.maxAltFt, units.altitude)))
          }
          {...(stats.maxAltFt === undefined ? {} : { unit: ALTITUDE_UNIT_LABEL[units.altitude] })}
        />
        <Tile
          label="Max speed"
          value={
            stats.maxGsKt === undefined
              ? '—'
              : formatCount(Math.round(convertSpeed(stats.maxGsKt, units.speed)))
          }
          {...(stats.maxGsKt === undefined ? {} : { unit: SPEED_UNIT_LABEL[units.speed] })}
        />
      </dl>

      <dl className="space-y-1 text-sm">
        {flight?.offBlock === undefined ? null : (
          <Row label="Off blocks" value={time(flight.offBlock)} />
        )}
        {summary.startTime === undefined ? null : (
          <Row label="Airborne" value={time(flight?.takeoff ?? summary.startTime)} />
        )}
        {summary.endTime === undefined ? null : (
          <Row label="Landed" value={time(flight?.landing ?? summary.endTime)} />
        )}
        {flight?.onBlock === undefined ? null : (
          <Row label="On blocks" value={time(flight.onBlock)} />
        )}
        {takeoffs === null ? null : (
          <Row label="Takeoffs" value={<span className="tabular">{takeoffs}</span>} />
        )}
        {landings === null ? null : (
          <Row
            label="Landings"
            value={
              <span className="tabular">
                {landings}
                {stats.touchAndGoCount === undefined || stats.touchAndGoCount === 0
                  ? ''
                  : ` · ${formatCount(stats.touchAndGoCount)} touch and go`}
              </span>
            }
          />
        )}
        {track === undefined ? null : (
          <>
            <Row
              label="Track points"
              value={<span className="tabular">{formatCount(track.pointCount)}</span>}
            />
            <Row label="Source" value={SOURCE_LABEL[track.source]} />
          </>
        )}
      </dl>
    </section>
  );
}
